import { ProductAddToCart } from 'src/components/restructure/product/ProductCard/ProductAddToCart'
import type { Product } from 'src/components/restructure/product/types/product'
import { formatterPrice } from 'src/utils/formatterPrice'
import { Image } from 'src/components/ui/Image'
import { Link } from 'gatsby'
import type { ReactNode } from 'react'
import { useProductLink } from 'src/sdk/product/useProductLinkV2'
import { MINICART_CROSS_SELL_EVENT_SOURCE } from 'src/components/cart/CartFeedback/constants'

import SimpleProductCardSelector from './SimpleProductCardSelector'

type SimpleProductCardProps = {
  product: Product
  showAddToCart?: boolean
  enableCardLink?: boolean
  fullWidth?: boolean
  showSelector?: boolean
  productBenefitsFn?: () => void
  onSelect?: React.Dispatch<React.SetStateAction<string | null>>
  selectedSku?: string | null
  shouldSelectSku?: boolean
  shelfRef?: string
  imageSize?: number
  customFnOnAdd?: (e: React.MouseEvent<HTMLButtonElement>) => void
  shelfEngine?: string
  backgroudOfCard?: string
  addToCartSource?: string
  addLocation?: string
}

const WrapperElement = ({
  children,
  enableCardLink,
  className,
  productLink,
}: {
  className?: string
  children: ReactNode
  enableCardLink: boolean
  productLink: { to: string; onClick: () => void; 'data-testid': string }
}) => {
  return enableCardLink ? (
    <Link {...productLink} className={className ?? ''}>
      {children}
    </Link>
  ) : (
    <div className={className ?? ''}>{children}</div>
  )
}

const SimpleProductCard = ({
  product,
  showAddToCart = true,
  enableCardLink = false,
  showSelector = false,
  fullWidth = false,
  productBenefitsFn,
  onSelect,
  selectedSku,
  shouldSelectSku,
  shelfRef = '',
  imageSize = 64,
  customFnOnAdd,
  shelfEngine,
  backgroudOfCard = '',
  addToCartSource,
  addLocation,
}: SimpleProductCardProps) => {
  const { items, productName, linkText } = product

  const firstAvailable = items.find((item) => item.isAvailable)

  const productLink = useProductLink({
    product,
    currentSku: firstAvailable,
    index: 0,
    slug: linkText,
    shelfRef,
    shelfEngine,
    ...(product?.slotId && { engine: 'dy' }),
  })

  if (!firstAvailable) {
    return null
  }

  const img = firstAvailable?.images[0]

  const {
    sellerOffer: { cashPrice, listPrice, cashDiscountPercentage },
  } = firstAvailable.sellerDefault

  return (
    <div className="relative">
      <div
        className={`py-3 border border-gray rounded-md ${backgroudOfCard} md:max-h-[109px]`}
      >
        <div
          className={`flex ${
            fullWidth ? 'w-full' : 'max-w-[354px]'
          } min-w-[300px]  min-h-[84px] items-center justify-between font-inter relative px-4`}
        >
          <WrapperElement
            className="flex gap-4 items-center"
            productLink={productLink}
            enableCardLink={enableCardLink}
          >
            <div
              style={{
                minWidth: `${imageSize}px`,
                minHeight: `${imageSize}px`,
              }}
              className="bg-neutral02"
            >
              <Image
                src={img.imageUrl}
                alt={img?.imageText ?? productName}
                className="mix-blend-multiply"
                width={imageSize}
                height={imageSize}
                sizes={`${imageSize}px`}
              />
            </div>
            <div className="w-full">
              <div className="flex gap-3">
                <p
                  className={`text-xs font-medium text-restructure-primary ${
                    fullWidth
                      ? 'max-w-[183px] sm:max-w-[500px] whitespace-nowrap  overflow-hidden'
                      : 'line-clamp-2'
                  } text-ellipsis`}
                >
                  {productName}
                </p>
                {productBenefitsFn && (
                  <button
                    type="button"
                    onClick={productBenefitsFn}
                    className="hidden md:block appearance-none bg-transparent border-none p-0 m-0 cursor-pointer text-restructure-primary underline text-xs font-normal"
                  >
                    Ver características
                  </button>
                )}
              </div>

              <p className="mt-1">
                <span className="text-sm font-bold">
                  {formatterPrice(cashPrice)}
                  {cashDiscountPercentage > 0 && ' à vista'}
                </span>
                {listPrice !== cashPrice && (
                  <span className="line-through text-xs text-redDiscount ml-1">
                    {formatterPrice(listPrice)}
                  </span>
                )}
              </p>
            </div>
          </WrapperElement>

          {showAddToCart && (
            <div className="ml-4">
              <ProductAddToCart
                Images={firstAvailable.images}
                product={product}
                currentSku={firstAvailable}
                origin="cart-feedback"
                eventSource={
                  addToCartSource ?? MINICART_CROSS_SELL_EVENT_SOURCE
                }
                addLocation={addLocation}
                customFnOnAdd={customFnOnAdd}
                title={shelfRef}
              />
            </div>
          )}
        </div>
        {showSelector && typeof onSelect === 'function' && (
          <SimpleProductCardSelector
            product={product}
            filters={['Cor']}
            onSelect={onSelect}
            selectedSku={selectedSku}
          />
        )}
      </div>
      {shouldSelectSku && !selectedSku && (
        <p className="text-restructure-error text-xs font-normal ml-4 mt-1 md:mt-3 absolute top-full left-0">
          Selecione uma opção
        </p>
      )}
      {productBenefitsFn && (
        <button
          type="button"
          onClick={productBenefitsFn}
          className="block md:hidden appearance-none bg-transparent border-none p-0 mt-3 mx-auto cursor-pointer text-restructure-primary underline text-xs font-normal"
        >
          Ver características do modelo
        </button>
      )}
    </div>
  )
}

export default SimpleProductCard
